import React, { useState } from 'react';
import StarRating from './StarRating';
import logoFB from '../../assets/review/logoFB.svg';
import logoGMB from '../../assets/review/logoGMB.svg';
import logoOS from '../../assets/review/logoOS.svg';
import logoRecommended from '../../assets/review/recommended.png';
import logoNotRecommended from '../../assets/review/not-recommended.png';
import { ReviewsHeaderProps } from '../type';
import backgroundImageReview from '../../assets/review/background_reviews.png';
import iconArrow from '../../assets/arrow-left.svg';
import ModalOpinionSystemConnect from '../components/ModalOpinionSystemConnect';
import CustomTooltip from './CustomTooltip';

const NetworkCardSkeleton = () => (
    <div className="flex items-center gap-[24px] bg-gray-200 p-[10px] rounded-[12px] animate-pulse">
        <div className="w-[36px] h-[36px] bg-gray-300 rounded-[6px]"></div>
        <div className="w-12 h-6 bg-gray-300 rounded"></div>
        <div className="flex flex-col gap-2">
            <div className="w-24 h-4 bg-gray-300 rounded"></div>
            <div className="w-20 h-3 bg-gray-300 rounded"></div>
        </div>
    </div>
);

const ReviewsHeader: React.FC<ReviewsHeaderProps> = ({
    platformRatings,
    isLoading,
    loadingState,
    onRefresh,
}) => {
    const gmbRating = platformRatings.find(r => r.platform === 'google_my_business');
    const fbRating = platformRatings.find(r => r.platform === 'facebook');
    const osRating = platformRatings.find(r => r.platform === 'opinion_system');
    const [showConnectionModal, setShowConnectionModal] = useState<boolean>(false);
    const userId = localStorage.getItem('userId') ? Number(localStorage.getItem('userId')) : null;

    const openModal = () => {
        setShowConnectionModal(true);
    };

    const handleSuccess = async () => {
        try {
            setShowConnectionModal(false);

            await new Promise(resolve => setTimeout(resolve, 2000));

            window.location.reload();
        } catch (error) {
            console.error('❌ Erreur lors du rafraîchissement :', error);
            window.location.reload();
        }
    };

    const hasAnyData =
        (gmbRating?.totalReviews || 0) > 0 ||
        (fbRating?.totalReviews || 0) > 0 ||
        (osRating?.totalReviews || 0) > 0;

    const hasAnyLoadingOrData =
        hasAnyData ||
        (loadingState && (loadingState.gmb || loadingState.facebook || loadingState.opinionSystem));

    if (isLoading || !hasAnyLoadingOrData) {
        return null;
    }

    return (
        <>
            {!isLoading && showConnectionModal && (
                <ModalOpinionSystemConnect
                    onClose={() => setShowConnectionModal(false)}
                    isLoading={isLoading}
                    setShowConnectionModal={setShowConnectionModal}
                    isConnected={true}
                    userId={userId}
                    onSuccess={handleSuccess}
                />
            )}

            <div
                className="flex items-center justify-between w-full p-6 border-b border-light-gray reviews rounded"
                style={{
                    backgroundImage: `
                      linear-gradient(270deg, rgba(255, 255, 255, 0) 16.5%, #FFF 46.44%),
                      url(${backgroundImageReview}),
                      linear-gradient(275deg, #62AA47 4.22%, var(--primary-color, #BED000) 96.79%)
                    `,
                    backgroundPosition: 'center, 50%, center',
                    backgroundSize: 'auto, cover, auto',
                    backgroundRepeat: 'no-repeat, no-repeat, no-repeat',
                    backgroundAttachment: 'scroll, scroll, scroll',
                    backgroundOrigin: 'padding-box, padding-box, padding-box',
                    backgroundClip: 'border-box, border-box, border-box',
                }}
            >
                <div className="flex gap-[20px]">
                    {((gmbRating?.totalReviews || 0) > 0 || loadingState?.gmb) &&
                        ((gmbRating?.totalReviews || 0) > 0 ? (
                            <div className="flex items-center gap-[24px] bg-primary-light p-[10px] rounded-[12px]">
                                <img
                                    className="flex justify-center items-center p-[6px] w-[36px] h-auto rounded-[6px] bg-white"
                                    src={logoGMB}
                                    alt="logo gmb"
                                />
                                <div className="text-heavy font-medium leading-[120%] text-dark-gray">
                                    {gmbRating
                                        ? gmbRating.rating.toFixed(1).replace('.', ',')
                                        : '-'}
                                </div>
                                <div className="text-heavy font-medium leading-[120%] text-dark-gray">
                                    <div className="visual-average-rating">
                                        <StarRating rating={gmbRating?.rating || 0}></StarRating>
                                    </div>
                                    <div className="text-more-small font-small leading-[120%] text-gray truncate">
                                        Basé sur {gmbRating?.totalReviews || 0} avis
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <NetworkCardSkeleton />
                        ))}

                    {((fbRating?.totalReviews || 0) > 0 || loadingState?.facebook) &&
                        ((fbRating?.totalReviews || 0) > 0 ? (
                            <div className="flex items-center gap-[24px] bg-primary-light p-[10px] rounded-[12px]">
                                <img
                                    className="flex justify-center items-center p-[6px] w-[36px] h-auto rounded-[6px] bg-white"
                                    src={logoFB}
                                    alt="logo fb"
                                />
                                <div className="text-heavy font-medium leading-[120%] text-dark-gray">
                                    {fbRating ? fbRating.rating + '%' : '-'}
                                </div>
                                <div className="right-average-card">
                                    <div className="visual-average-rating">
                                        {fbRating ? (
                                            fbRating.rating >= 50 ? (
                                                <img src={logoRecommended} alt="Recommended" />
                                            ) : (
                                                <img
                                                    src={logoNotRecommended}
                                                    alt="Not Recommended"
                                                />
                                            )
                                        ) : (
                                            '-'
                                        )}
                                    </div>
                                    <div className="text-more-small font-small leading-[120%] text-gray truncate mt-0.5">
                                        Vous recommande ({fbRating?.totalReviews || 0} avis)
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <NetworkCardSkeleton />
                        ))}

                    {((osRating?.totalReviews || 0) > 0 || loadingState?.opinionSystem) &&
                        ((osRating?.totalReviews || 0) > 0 ? (
                            <div className="flex items-center gap-[24px] bg-primary-light p-[10px] rounded-[12px]">
                                <img
                                    className="flex justify-center items-center p-[6px] w-[36px] h-auto rounded-[6px] bg-white"
                                    src={logoOS}
                                    alt="logo os"
                                />
                                <div className="text-heavy font-medium leading-[120%] text-dark-gray">
                                    {osRating ? osRating.rating.toFixed(1).replace('.', ',') : '-'}
                                </div>
                                <div className="text-heavy font-medium leading-[120%] text-dark-gray">
                                    <div className="visual-average-rating">
                                        <StarRating rating={osRating?.rating || 0}></StarRating>
                                    </div>
                                    <div className="text-more-small font-small leading-[120%] text-gray truncate">
                                        Basé sur {osRating?.totalReviews || 0} avis
                                    </div>
                                </div>
                                <CustomTooltip
                                    message="Modifier votre numéro de SIRET"
                                    delay={500}
                                    position="bottom"
                                    backgroundColor="var(--primary-light)"
                                    textColor="var(--dark-gray)"
                                    arrowColor="#E5E5E5"
                                >
                                    <div
                                        className="flex justify-center items-center gap-2 [background:var(--Couleur-LBI-Vert-LBI-Vert-LBI---500,#BED000)] px-4 py-1.5 rounded-[40px] React cursor-pointer"
                                        onClick={openModal}
                                    >
                                        <img
                                            className="w-6 h-6 rotate-180"
                                            src={iconArrow}
                                            alt="flêche"
                                        />
                                    </div>
                                </CustomTooltip>
                            </div>
                        ) : (
                            <NetworkCardSkeleton />
                        ))}
                </div>
            </div>
        </>
    );
};

export default ReviewsHeader;
